import { isCodeModuleAvailable } from "./capabilities"
import { requestNestMethod } from "../nest/client"
import { downloadNestAttachment, downloadNestAttachmentRange, uploadNestAttachment } from "../nest/attachments"

export interface SnapshotPatchFile {
    id: string
    path: string
    oldPath?: string
    status: "added" | "deleted" | "modified" | "renamed"
    binary: boolean
    insertions: number
    deletions: number
    changedLines: number
    hunkCount: number
    patchStart: number
    patchEnd: number
}

export interface SnapshotPatchIndex {
    version: 1
    patchSize: number
    files: SnapshotPatchFile[]
}

interface SnapshotPatchAttachmentRecord {
    version: 2
    patchAttachmentId: string
    patchSize: number
    files: SnapshotPatchFile[]
}

const NAMESPACE = "wildlark.snapshots"
const encoder = new TextEncoder()
const decoder = new TextDecoder()

function sanitizeId(id: string): string {
    if (!/^[a-zA-Z0-9_-]+$/.test(id)) throw new Error("Invalid snapshot ID")
    return id
}

function indexKey(id: string): string {
    return `${sanitizeId(id)}/index`
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isSnapshotPatchFile(value: unknown): value is SnapshotPatchFile {
    if (!isRecord(value)) return false
    return (
        typeof value.id === "string" &&
        typeof value.path === "string" &&
        (value.oldPath === undefined || typeof value.oldPath === "string") &&
        (value.status === "added" || value.status === "deleted" || value.status === "modified" || value.status === "renamed") &&
        typeof value.binary === "boolean" &&
        typeof value.insertions === "number" &&
        typeof value.deletions === "number" &&
        typeof value.changedLines === "number" &&
        typeof value.hunkCount === "number" &&
        typeof value.patchStart === "number" &&
        typeof value.patchEnd === "number"
    )
}

function isSnapshotPatchAttachmentRecord(value: unknown): value is SnapshotPatchAttachmentRecord {
    return (
        isRecord(value) &&
        value.version === 2 &&
        typeof value.patchAttachmentId === "string" &&
        typeof value.patchSize === "number" &&
        Array.isArray(value.files) &&
        value.files.every(isSnapshotPatchFile)
    )
}

function patchIndexFromAttachmentRecord(record: SnapshotPatchAttachmentRecord): SnapshotPatchIndex {
    return {
        version: 1,
        patchSize: record.patchSize,
        files: record.files,
    }
}

async function readAppData(key: string): Promise<unknown> {
    const result = await requestNestMethod("appData.get", { namespace: NAMESPACE, key })
    return result.found ? (result.value ?? null) : null
}

async function loadAttachmentRecord(id: string): Promise<SnapshotPatchAttachmentRecord | null> {
    const value = await readAppData(indexKey(id))
    return isSnapshotPatchAttachmentRecord(value) ? value : null
}

async function saveBundle(id: string, patch: string, index: SnapshotPatchIndex): Promise<void> {
    const safeId = sanitizeId(id)
    const patchBytes = encoder.encode(patch)
    const attachment = await uploadNestAttachment(patchBytes, { mediaType: "text/x-patch", filename: `${safeId}.patch` })
    try {
        await requestNestMethod("appData.set", {
            namespace: NAMESPACE,
            key: indexKey(safeId),
            value: {
                version: 2,
                patchAttachmentId: attachment.attachmentId,
                patchSize: patchBytes.byteLength,
                files: index.files,
            } satisfies SnapshotPatchAttachmentRecord,
        })
    } catch (error) {
        await requestNestMethod("attachment.delete", { attachmentId: attachment.attachmentId }).catch((cleanupError) => {
            console.warn("[snapshotsApi] Failed to clean up snapshot patch attachment after appData write failure:", cleanupError)
        })
        throw error
    }
}

async function loadPatch(id: string): Promise<string | null> {
    const attachmentRecord = await loadAttachmentRecord(id)
    if (attachmentRecord) {
        const downloaded = await downloadNestAttachment(attachmentRecord.patchAttachmentId)
        return decoder.decode(downloaded.data)
    }
    return null
}

async function loadIndex(id: string): Promise<SnapshotPatchIndex | null> {
    const value = await readAppData(indexKey(id))
    return isSnapshotPatchAttachmentRecord(value) ? patchIndexFromAttachmentRecord(value) : null
}

async function loadPatchSlice(id: string, start: number, end: number): Promise<string | null> {
    if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end < start) {
        throw new Error("Invalid patch slice range")
    }
    if (start === end) return ""

    const attachmentRecord = await loadAttachmentRecord(id)
    if (attachmentRecord) {
        if (end > attachmentRecord.patchSize) throw new Error("Patch slice exceeds file size")
        const downloaded = await downloadNestAttachmentRange(attachmentRecord.patchAttachmentId, start, end)
        return decoder.decode(downloaded.data)
    }

    return null
}

async function deleteBundle(id: string): Promise<void> {
    const safeId = sanitizeId(id)
    const attachmentRecord = await loadAttachmentRecord(safeId)
    if (attachmentRecord) {
        await requestNestMethod("attachment.delete", { attachmentId: attachmentRecord.patchAttachmentId })
    }
    await requestNestMethod("appData.delete", { namespace: NAMESPACE, key: indexKey(safeId) })
}

function isAvailable(): boolean {
    return isCodeModuleAvailable()
}

export const snapshotsApi = {
    saveBundle,
    loadPatch,
    loadIndex,
    loadPatchSlice,
    delete: deleteBundle,
    isAvailable,
}
